import React from "react";
import "./InuaLoader.css";

// --- INUA BRAND LOADER ---
// Recreates the technique behind SelfMadeSystem's animated You.com logo
// (https://blog.shoghisimon.cc/how-i-made-you/) — an SVG path with
// pathLength="360" animated via stroke-dasharray/stroke-dashoffset — but
// swaps the hand-traced "YOU" letterforms (which belong to You.com's logo,
// not something we should be reproducing) for our own wordmark + a
// two-ring spinner built from the exact two animation styles described in
// that post:
//   .dash  -> "comet" stroke that draws itself forward around the ring
//             (dashArray keyframes)
//   .spin  -> the oscillating Material-style arc, plus a stepped rotation
//             (spinDashArray + spin keyframes)
// Both rings share one <linearGradient>, so "INUA" and the spinner always
// read as one coherent brand mark rather than two separately-colored bits.
//
// Three ready-made variants cover every place this loader shows up:
//   "buyer"  — buyer-side screens/transitions (deep blue -> soft periwinkle,
//              reusing the same "soft" tone EntryScreen already uses for
//              buyer copy, so it matches without introducing a new color)
//   "seller" — seller-side screens/transitions (amber -> soft gold, same
//              idea using EntryScreen's seller "soft" tone)
//   "app"    — anywhere generic in-app (Orders, Wishlist, etc.) — the
//              explicit brand gradient from #1006C5 to #ffb800
export type InuaLoaderVariant = "buyer" | "seller" | "app";

const VARIANT_STOPS: Record<InuaLoaderVariant, { from: string; to: string }> = {
  buyer: { from: "#1006C5", to: "#c7c3ff" }, // matches THEME.buyer.soft in App.tsx
  seller: { from: "#FFB800", to: "#ffe6a8" }, // matches THEME.seller.soft in App.tsx
  app: { from: "#1006C5", to: "#ffb800" },
};

export interface InuaLoaderProps {
  /** Which gradient to use — see VARIANT_STOPS above. Default "app". */
  variant?: InuaLoaderVariant;
  /** Renders as a full-height centered takeover instead of an inline mark. */
  fullScreen?: boolean;
  /** Optional line of text under the mark (e.g. "One moment"). */
  message?: string;
  /** Scales the whole mark down for tight spaces. Default 1 = 96px tall. */
  scale?: number;
  /** Explicit override for either gradient stop — takes priority over `variant`. */
  fromColor?: string;
  toColor?: string;
}

let gradientIdSeq = 0;

export default function InuaLoader({
  variant = "app",
  fullScreen = false,
  message,
  scale = 1,
  fromColor,
  toColor,
}: InuaLoaderProps) {
  const stops = VARIANT_STOPS[variant];
  const from = fromColor || stops.from;
  const to = toColor || stops.to;

  // Each mounted instance needs its own gradient id — <linearGradient>
  // ids are global to the document, so two loaders on screen at once
  // would otherwise silently share (and fight over) the same gradient.
  const gradIdRef = React.useRef<string>(`inua-loader-grad-${gradientIdSeq++}`);
  const gradId = gradIdRef.current;

  const nativeHeight = 96;
  const scaledHeight = nativeHeight * scale;

  const mark = (
    <div className="inua-loader-stack">
      <span
        className="inua-loader__scale-box"
        style={
          scale !== 1 ? { height: scaledHeight } : { height: nativeHeight }
        }
      >
        <span
          className="inua-loader"
          style={
            scale !== 1
              ? { transform: `scale(${scale})`, transformOrigin: "top left" }
              : undefined
          }
        >
          <svg
            className="inua-loader__ring"
            viewBox="0 0 64 64"
            width={64}
            height={64}
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={from} />
                <stop offset="100%" stopColor={to} />
              </linearGradient>
            </defs>
            {/* Outer ring — the Material-style oscillating arc ("spin") */}
            <path
              className="inua-loader__path inua-loader__path--spin"
              stroke={`url(#${gradId})`}
              strokeWidth="6"
              strokeLinecap="round"
              pathLength={360}
              d="M 32 32
                 m 0 -27
                 a 27 27 0 1 1 0 54
                 a 27 27 0 1 1 0 -54"
            />
            {/* Inner ring — the "comet" stroke that draws itself forward
                ("dash") — same technique used for the Y/U letters in the
                original You.com recreation, applied here to a second ring
                instead of hand-traced letterforms. */}
            <path
              className="inua-loader__path inua-loader__path--dash"
              stroke={`url(#${gradId})`}
              strokeWidth="3"
              strokeLinecap="round"
              pathLength={360}
              opacity={0.55}
              d="M 32 32
                 m 0 -18
                 a 18 18 0 1 1 0 36
                 a 18 18 0 1 1 0 -36"
            />
          </svg>

          <span
            className="inua-loader__word"
            style={{
              backgroundImage: `linear-gradient(90deg, ${from}, ${to}, ${from})`,
            }}
          >
            INUA
          </span>
        </span>
      </span>
      {message && <span className="inua-loader__message">{message}</span>}
    </div>
  );

  if (!fullScreen) return mark;

  return <div className="inua-loader-fullscreen">{mark}</div>;
}
